
import Vue from 'vue';
import type { PropOptions } from 'vue';
import type { Location } from 'vue-router';
import { mapActions, mapGetters, mapState } from 'vuex';
import PortfolioEditJobsInquirySectionModal from './PortfolioEditJobsInquirySectionModal.vue';
import PublicPortfolioHeadline from '@/components/partials/public-portfolio/PublicPortfolioHeadline/PublicPortfolioHeadline.vue';
import PortfolioBannerWelcome from '@/components/partials/public-portfolio/PortfolioBannerWelcome/PortfolioBannerWelcome.vue';
import PortfolioBannerYourPortfolioIsVisible from '@/components/partials/public-portfolio/PortfolioBannerYourPortfolioIsVisible/PortfolioBannerYourPortfolioIsVisible.vue';
import PortfolioButton from '@/components/partials/public-portfolio/PortfolioButton/PortfolioButton.vue';
import PublicPortfolioEditPersonalInfoModal from '@/components/partials/public-portfolio/PublicPortfolioEditPersonalInfoModal/PublicPortfolioEditPersonalInfoModal.vue';
import PortfolioUpdateCurrentUserMutation from '@/apollo/mutations/Portfolio/PortfolioUpdateCurrentUser.mutation';
import PublicPortfolioShareModal from '@/components/partials/public-portfolio/PublicPortfolioShareModal.vue';
import UpdateConfigurationForPublicPortfolioCurrentUserMutation from '@/apollo/mutations/Portfolio/UpdateConfigurationForPublicPortfolioCurrentUser.mutation';
import PortfolioVisibilityIndicator from '@/components/partials/public-portfolio/PortfolioVisibilityIndicator/PortfolioVisibilityIndicator.vue';
import PortfolioEditSalesExperienceModal from '@/components/partials/public-portfolio/PortfolioEditSalesExperienceModal/PortfolioEditSalesExperienceModal.vue';
import PortfolioJobsInquirySection from '@/components/partials/public-portfolio/PortfolioJobsInquirySection/PortfolioJobsInquirySection.vue';
import type {
  JobsProfileConfig,
  JobsSection,
} from '@/components/partials/public-portfolio/PortfolioJobsInquirySection/PortfolioJobsInquirySectionProfile/types';
import { getProfileConfig } from '@/components/partials/public-portfolio/PortfolioJobsInquirySection/PortfolioJobsInquirySectionProfile/utils';
import PortfolioEditUpdateJobsInquiryMutation from '@/components/partials/portfolio/PortfolioEditUpdateJobsInquiry.mutation';
import { toCamelTransformator } from '@/components/utils/core/object';
import PublicPortfolioSection from '~/components/partials/public-portfolio/PublicPortfolioSection.vue';
import PublicPortfolioSalesExperience from '~/components/partials/public-portfolio/PublicPortfolioSalesExperience/PublicPortfolioSalesExperience.vue';
import PublicPortfolioWorkHistorySection from '~/components/partials/public-portfolio/PublicPortfolioWorkHistory/PublicPortfolioWorkHistorySection.vue';
import PublicPortfolioAwardsSection from '~/components/partials/public-portfolio/PublicPortfolioAwards/PublicPortfolioAwardsSection.vue';
import PublicPortfolioClientsSection from '~/components/partials/public-portfolio/PublicPortfolioClients/PublicPortfolioClientsSection.vue';
import PublicPortfolioPostsSection from '~/components/partials/public-portfolio/PublicPortfolioPostsSection/PublicPortfolioPostsSection.vue';
import PublicPortfolioHero from '~/components/partials/public-portfolio/PublicPortfolioHero/PublicPortfolioHero.vue';
import PlusIcon from '~/assets/svg/plus.vue.svg';
import PublicPortfolioLinkedinImportModal from '~/components/partials/public-portfolio/PublicPortfolioLinkedinImportModal.vue';
import PublicPortfolioLinkedinErrorModal from '~/components/partials/public-portfolio/PublicPortfolioLInkedinErrorModal.vue';
import BravadoButtonV3 from '~/components/common/BravadoButtonV3/BravadoButtonV3.vue';
import PublicPortfolioPage from '~/components/partials/public-portfolio/PublicPortfolioPage.vue';
import BravadoRightNavigation from '~/components/common/BravadoRightNavigation/BravadoRightNavigation.vue';
import type {
  PublicPortfolioCurrentUser,
  CurrentUser,
  UpdateCurrentUserInput,
  UpdateConfigurationForPublicPortfolioCurrentUserInput,
  JobsInquiry,
  UpdateInquiryInput,
  Testimonial,
  PublicPortfolioConfiguration,
  PostConnection,
} from '~/types/graphql';
import publicPortfolioSectionMixin from '~/components/partials/public-portfolio/mixins/publicPortfolioSectionMixin';
import PublicPortfolioTestimonialsSection from '~/components/partials/public-portfolio/PublicPortfolioTestimonials/PublicPortfolioTestimonialsSection.vue';
import { TAB_KEYS } from '~/components/partials/public-portfolio/PublicPortfolioTestimonials/constants';
import publicPortfolioTestimonialMixin from '~/components/partials/public-portfolio/PublicPortfolioTestimonials/mixins/publicPortfolioTestimonialMixin';
import type { PublicPortfolioTestimonialMixinType } from '~/components/partials/public-portfolio/PublicPortfolioTestimonials/mixins/publicPortfolioTestimonialMixin';
import { PublicPortfolioCurrentUserQuery } from '~/apollo/queries/PublicPortfolio/PublicPortfolioCurrentUser.query';
import PublicPortfolioPrivacyModal from '~/components/partials/public-portfolio/PublicPortfolioHeadline/PublicPortfolioPrivacyModal.vue';
import PublicPortfolioEducationHistorySection from '~/components/partials/public-portfolio/PublicPortfolioEducationHistorySection/PublicPortfolioEducationHistorySection.vue';
import JobsProfileUserProfile from '@/components/partials/jobs/JobsProfile/JobsProfileUserProfile/JobsProfileUserProfile.vue';
import JobsProfileJobSearchStatus from '@/components/partials/jobs/JobsProfile/JobsProfileJobSearchStatus/JobsProfileJobSearchStatus.vue';

import { jobSearchStatusKeys } from '@/components/partials/war-room-onboarding/constants';
import RenewActiveJobStatusMutation from '@/apollo/mutations/RenewActiveJobStatus.mutation';
import type { IOption } from '@/components/partials/jobs/JobsProfile/JobsProfileJobSearchStatus/types';
import PortfolioCurrentUserQuery from '@/apollo/queries/Portfolio/PortfolioCurrentUser.query';
import ActivateJobsInquiryMutation from '@/apollo/mutations/Jobs/ActivateJobsInquiry.mutation';
import { JobsInquiryActivationOrigin } from '@/types/graphql';
import { modalNames } from '@/components/partials/jobs/constants';
import JobsDesiredCriteriaModal from '@/components/partials/jobs/JobsDesiredCriteriaModal/JobsDesiredCriteriaModal.vue';

export default (Vue as PublicPortfolioTestimonialMixinType).extend({
  name: 'PortfolioEdit',
  components: {
    PublicPortfolioTestimonialsSection,
    PublicPortfolioPage,
    PublicPortfolioLinkedinErrorModal,
    PublicPortfolioLinkedinImportModal,
    PortfolioButton,
    PublicPortfolioHero,
    PublicPortfolioClientsSection,
    PublicPortfolioWorkHistorySection,
    PublicPortfolioSalesExperience,
    PublicPortfolioSection,
    PublicPortfolioAwardsSection,
    BravadoButtonV3,
    PlusIcon,
    BravadoRightNavigation,
    PublicPortfolioHeadline,
    PortfolioBannerYourPortfolioIsVisible,
    PortfolioBannerWelcome,
    PublicPortfolioShareModal,
    PublicPortfolioPrivacyModal,
    PublicPortfolioEditPersonalInfoModal,
    PortfolioVisibilityIndicator,
    PortfolioEditSalesExperienceModal,
    PortfolioJobsInquirySection,
    PortfolioEditJobsInquirySectionModal,
    PublicPortfolioEducationHistorySection,
    PublicPortfolioPostsSection,
    JobsProfileUserProfile,
    JobsProfileJobSearchStatus,
    JobsDesiredCriteriaModal,
  },
  mixins: [publicPortfolioSectionMixin, publicPortfolioTestimonialMixin],
  props: {
    currentUser: {
      type: Object,
      required: true,
    } as PropOptions<CurrentUser>,
    portfolioCurrentUser: {
      type: Object,
      required: true,
    } as PropOptions<PublicPortfolioCurrentUser>,
    jobsInquiry: {
      type: Object,
      default: () => ({} as JobsInquiry),
    } as PropOptions<JobsInquiry>,
    testifySign: {
      type: String,
      required: true,
    } as PropOptions<string>,
    publicPosts: {
      type: Object,
      required: true,
    } as PropOptions<PostConnection>,
  },
  data() {
    return {
      inquiryActivationFired: false,
      modalNames,
    };
  },
  computed: {
    ...mapState('portfolio', ['user', 'isPersonal', 'linkedInImportState']),
    ...mapGetters('portfolio', [
      'hasClients',
      'hasAwards',
      'hasSalesExperience',
      'hasWorkHistories',
      'hasDescription',
      'isPortfolioEmpty',
    ]),
    configuration(): PublicPortfolioConfiguration {
      return this.portfolioCurrentUser.configuration;
    },
    testimonials(): Testimonial[] {
      return this.portfolioCurrentUser.testimonials || [];
    },
    profileConfig(): JobsProfileConfig {
      const profileConfigGeneratorPayload = {
        inquiry: this.jobsInquiry,
        user: this.currentUser,
      };

      return getProfileConfig(profileConfigGeneratorPayload);
    },
    profileConfigSections(): Record<string, JobsSection> {
      // @ts-ignore
      const [deals, salary] = this.profileConfig;

      return { deals, salary };
    },
    testimonialApproveRoute(): Location {
      return {
        query: {
          dynamic_modal: this.$constants.dynamicModals.PUBLIC_PORTFOLIO_EDIT_TESTIMONIALS_MODAL,
          tab: TAB_KEYS.actionNeeded,
        },
      };
    },
    hasEducationHistories(): boolean {
      return Boolean(this.portfolioCurrentUser.educationHistories?.length);
    },
  },
  methods: {
    ...mapActions('portfolio', { fetchProfile: 'load' }),
    async updateProfile({
      modalName,
      userAttrs,
      configurationAttrs,
      inquiryAttrs,
    }: {
      modalName: string;
      userAttrs: Partial<UpdateCurrentUserInput>;
      configurationAttrs: Partial<UpdateConfigurationForPublicPortfolioCurrentUserInput>;
      inquiryAttrs: Partial<UpdateInquiryInput>;
    }): Promise<void> {
      try {
        if (modalName) {
          this.$modal.hide(modalName);
        }

        const updates = [];

        if (userAttrs) {
          // @ts-ignore
          updates.push(this.updateUserFields(userAttrs));
        }

        if (configurationAttrs) {
          // @ts-ignore
          updates.push(this.updateConfigurationFields(configurationAttrs));
        }

        if (inquiryAttrs) {
          // @ts-ignore
          updates.push(this.updateInquiryFields(inquiryAttrs));
        }

        await Promise.all(updates);

        // @ts-ignore
        if (userAttrs?.slug !== this.$route.params.slug) return;

        // @ts-ignore
        await this.fetchProfile({ slug: this.$route.params.slug });
      } catch (e) {
        setTimeout(() => {
          if (modalName) {
            this.$modal.show(modalName);
          }
        }, 300);

        this.$notice.error('Failed to update', e);
        this.$sentryUtils.error(e);
      }
    },
    async updateUserFields(input: Partial<UpdateCurrentUserInput>): Promise<void> {
      if (!input) return;

      await this.$apollo.mutate({
        mutation: PortfolioUpdateCurrentUserMutation,
        variables: {
          input,
        },
        optimisticResponse: {
          __typename: 'Mutation',
          updateCurrentUser: {
            __typename: 'CurrentUser',
            ...this.currentUser,
            ...input,
          },
        },
      });
    },
    async updateConfigurationFields(
      input: Partial<UpdateConfigurationForPublicPortfolioCurrentUserInput>,
    ): Promise<void> {
      if (!input) return;

      await this.$apollo.mutate({
        mutation: UpdateConfigurationForPublicPortfolioCurrentUserMutation,
        variables: {
          input,
        },
        update: (store, { data: { updateConfigurationForPublicPortfolioCurrentUser } }) => {
          const { publicPortfolioCurrentUser } = store.readQuery({
            query: PublicPortfolioCurrentUserQuery,
          }) as { publicPortfolioCurrentUser: PublicPortfolioCurrentUser };

          store.writeQuery({
            query: PublicPortfolioCurrentUserQuery,
            data: {
              publicPortfolioCurrentUser: {
                __typename: 'PublicPortfolioCurrentUser',
                ...publicPortfolioCurrentUser,
                configuration: {
                  __typename: 'PublicPortfolioConfiguration',
                  ...publicPortfolioCurrentUser.configuration,
                  ...updateConfigurationForPublicPortfolioCurrentUser,
                },
              },
            },
          });
        },
      });

      (this.$refs.hero as Vue & { refetch: () => void }).refetch();
    },
    async updateInquiryFields(input: Partial<UpdateInquiryInput>): Promise<void> {
      if (!input) return;

      await this.$apollo.mutate({
        mutation: PortfolioEditUpdateJobsInquiryMutation,
        variables: {
          input: {
            ...toCamelTransformator(input),
          },
        },
        optimisticResponse: {
          __typename: 'Mutation',
          updateJobsInquiry: {
            __typename: 'JobsInquiry',
            ...this.jobsInquiry,
            lastActiveAt: new Date().toString(),
          },
        },
      });
    },
    openModal(modalName: string): void {
      this.$modal.showWithRoute({ custom: modalName });
    },
    handleEditField(section: string, field: string): void {
      if (!field) {
        // @ts-ignore
        this.openModal(section);
        return;
      }

      this.$modal.showWithRoute({ custom: section }, { field_key: field });
    },
    handleEducationEdit(education: any): void {
      this.$router.push({
        query: { dynamic_modal: this.$constants.dynamicModals.PUBLIC_PORTFOLIO_EDUCATION_MODAL, id: education.id },
      });
    },
    handleEducationAdd(): void {
      this.$router.push({
        query: { dynamic_modal: this.$constants.dynamicModals.PUBLIC_PORTFOLIO_EDUCATION_MODAL },
      });
    },
    getActivationOrigin(): string {
      switch (this.$route.query.activation_origin) {
        case 'sms':
          return JobsInquiryActivationOrigin.Sms;
        case 'email':
          return JobsInquiryActivationOrigin.Email;
        default:
          return JobsInquiryActivationOrigin.ProfileUpdate;
      }
    },
    async activateInquiry(): Promise<void> {
      if (this.inquiryActivationFired) return;

      this.inquiryActivationFired = true;

      const activationOrigin = this.getActivationOrigin();

      try {
        await this.$apollo.mutate({
          mutation: ActivateJobsInquiryMutation,
          variables: {
            input: {
              activationOrigin,
            },
          },
          optimisticResponse: {
            __typename: 'Mutation',
            activateJobsInquiry: {
              __typename: 'JobsInquiry',
              id: this.jobsInquiry?.id,
              lastActiveAt: new Date().toString(),
              activable: false,
            },
          },
        });
      } catch (e) {
        this.$notice.error('Failed to activate the Inquiry', e);
        this.$sentryUtils.error(e);
      } finally {
        this.inquiryActivationFired = false;
      }
    },
    async handleChangeJobsSearchStatus(status: IOption): Promise<void> {
      try {
        if (status.key !== jobSearchStatusKeys.NOT_INTERESTED) {
          await this.activateInquiry();
        }

        await this.updateUserFields({ warRoomJobSearchStatus: status.key });
      } catch (e) {
        this.$notice.error('Failed to save status', e);
        this.$sentryUtils.error(e);
      }
    },
    async handleRenewJobSearchStatus(): Promise<void> {
      try {
        await this.$apollo.mutate({
          mutation: RenewActiveJobStatusMutation,
          variables: {
            input: {},
          },
          update: (
            store,
            {
              data: {
                renewActiveJobStatus: { warRoomJobSearchStatusExpiredAt, warRoomJobSearchStatus },
              },
            },
          ) => {
            const query = {
              query: PortfolioCurrentUserQuery,
            };
            const data = store.readQuery<{ currentUser: CurrentUser }>(query);

            if (!data) return;

            data.currentUser = { ...(data.currentUser || {}), warRoomJobSearchStatusExpiredAt, warRoomJobSearchStatus };
            store.writeQuery({ ...query, data });
          },
        });
      } catch (e) {
        this.$notice.error('Failed to renew', e);
        this.$sentryUtils.error(e);
      }
    },
  },
});
